import React, { useCallback, useEffect, useRef, useState } from "react";
import { CButton, CButtonGroup, CFormInput, CFormSelect, CTooltip } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";

import { useRunDetail } from "@src/contexts/RunDetailContext";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useQuoteCurrency } from "@src/hooks/useQuoteCurrency";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import { COMMAND_POLL_MAX_ATTEMPTS, EXECUTION_POLL_DELAY_MS } from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";
import { type FuturesDirection, getLeverage, isFuturesMode } from "@src/utils/executionUtils";

// TODO: Extract this polling logic into a shared useCommandPolling hook.
// This pattern is repeated in EndExecutionButtons.tsx, OrderCommandForm.tsx, ClosePositionButton.tsx,
// and CancelLimitOrderButton.tsx.
// The hook should accept: executionId, callbacks (onComplete, onFail), options (maxAttempts, interval),
// and return: { pollCommand, isPolling }.

interface Props {
  executionId: number;
  exchangeName: string;
  symbolName: string;
  rowIndex: number;
  /** Run data for detecting futures mode. Accepts both backtest and execution types. */
  run?: T.ApiRun;
}

type AmountType = "quote" | "position";
type OrderType = "market" | "limit";

export const ManualOrderRow: React.FC<Props> = ({
  executionId,
  exchangeName,
  symbolName,
  rowIndex,
  run,
}) => {
  const [amountType, setAmountType] = useState<AmountType>("quote");
  const [amount, setAmount] = useState("");
  const [orderType, setOrderType] = useState<OrderType>("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [direction, setDirection] = useState<FuturesDirection>("long");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollAbortRef = useRef(false);

  const quoteCurrency = useQuoteCurrency(exchangeName, symbolName) ?? "";
  const isFutures = isFuturesMode(run);
  const leverage = getLeverage(run);
  const showLeverageHint = isFutures && amountType === "quote" && leverage !== null && leverage > 1;

  // Cleanup on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
    };
  }, []);

  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { updateRun } = useRunDetail();

  // Poll command status until terminal state
  const pollCommandStatus = useCallback(
    async (commandId: number) => {
      setPolling(true);
      pollAbortRef.current = false;
      let reachedTerminalState = false;

      for (let attempt = 0; attempt < COMMAND_POLL_MAX_ATTEMPTS; attempt++) {
        if (pollAbortRef.current) break;

        await delay(EXECUTION_POLL_DELAY_MS);

        try {
          const command = await tdApi.getExecutionCommand(executionId, commandId);
          if (!command) continue;

          if (command.status === "completed") {
            reachedTerminalState = true;
            const result = command.result as { position_id?: number; movement_id?: number } | null;
            console.log("[ManualOrderRow] Command completed:", {
              commandId,
              executionId,
              result,
              command,
            });
            showToast({
              type: "success",
              title: `Command #${commandId} Executed`,
              message: result?.position_id
                ? `Position #${result.position_id} created`
                : "Order placed successfully",
              timeout: 8000,
            });

            // Fetch full execution with positions and update Redux
            try {
              const execution = await tdApi.getExecution(executionId, {
                embed: "positions(movements(order))",
              });
              if (execution) {
                updateRun(execution);
                console.log(
                  "[ManualOrderRow] Execution refreshed in Redux, positions:",
                  execution.positions?.length,
                );
              }
            } catch (err) {
              console.error("[ManualOrderRow] Failed to refresh execution:", err);
            }

            // Reset form on success
            setAmount("");
            setLimitPrice("");
            setNote("");
            break;
          } else if (command.status === "failed") {
            reachedTerminalState = true;
            console.log("[ManualOrderRow] Command failed:", {
              commandId,
              executionId,
              error: command.error_message,
              command,
            });
            showToast({
              type: "danger",
              title: `Command #${commandId} Failed`,
              message: command.error_message || "Unknown error",
              timeout: 15000,
            });
            break;
          }
          // Still processing, continue polling
        } catch (err) {
          console.error("[ManualOrderRow] Polling error:", err);
        }
      }

      // Notify user if polling timed out without reaching a terminal state
      if (!reachedTerminalState && !pollAbortRef.current) {
        console.warn("[ManualOrderRow] Polling timed out:", {
          commandId,
          executionId,
          maxAttempts: COMMAND_POLL_MAX_ATTEMPTS,
          totalTimeMs: COMMAND_POLL_MAX_ATTEMPTS * EXECUTION_POLL_DELAY_MS,
        });
        showToast({
          type: "warning",
          title: `Command #${commandId} Timeout`,
          message: `Command is still processing. Check worker logs or try again later.`,
          timeout: 15000,
        });
      }

      if (!pollAbortRef.current) {
        setPolling(false);
      }
    },
    [executionId, showToast, updateRun],
  );

  const handleSubmit = useCallback(async () => {
    // Validate inputs
    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      showToast({
        type: "warning",
        title: "Invalid Amount",
        message: "Please enter a valid amount greater than 0",
        timeout: 5000,
      });
      return;
    }

    let price: number | undefined;
    if (orderType === "limit") {
      price = parseFloat(limitPrice);
      if (isNaN(price) || price <= 0) {
        showToast({
          type: "warning",
          title: "Invalid Price",
          message: "Please enter a valid limit price greater than 0",
          timeout: 5000,
        });
        return;
      }
    }

    // Build labels once for reuse in confirmation and toast
    const directionLabel = isFutures ? (direction === "long" ? "Long" : "Short") : "Buy";
    const orderTypeLabel = orderType === "market" ? "Market" : "Limit";
    const priceLabel = orderType === "limit" && price ? ` @ ${price}` : "";
    const amountLabel =
      amountType === "quote"
        ? `${amountValue} ${quoteCurrency}`
        : `${amountValue} ${isFutures ? "contracts" : "units"}`;

    // Build confirmation message and determine command type
    let confirmMsg: string;
    let commandType: T.TDT.ExecutionCommandType;

    if (isFutures) {
      // Futures: Open Long or Open Short
      const dirLabel = direction === "long" ? "Open Long" : "Open Short";
      confirmMsg = `${dirLabel} ${orderTypeLabel}${priceLabel} order for ${amountLabel} on ${symbolName}?\n\nThis will open a new ${direction} futures position.`;

      if (direction === "long") {
        commandType =
          orderType === "market"
            ? "place-futures-long-market-order"
            : "place-futures-long-limit-order";
      } else {
        commandType =
          orderType === "market"
            ? "place-futures-short-market-order"
            : "place-futures-short-limit-order";
      }
    } else {
      // Spot: BUY
      confirmMsg = `Place BUY ${orderTypeLabel}${priceLabel} order for ${amountLabel} on ${symbolName}?\n\nThis will open a new position.`;
      commandType = orderType === "market" ? "place-spot-market-order" : "place-spot-limit-order";
    }

    const { confirmed } = await confirmModal(confirmMsg);
    if (!confirmed) return;

    setLoading(true);
    try {
      const payload: T.TDT.PlaceOrderPayload = {
        side: isFutures ? (direction === "long" ? "buy" : "sell") : "buy",
        amount_type: amountType,
        amount_value: amountValue,
        ...(orderType === "limit" && price !== undefined && { limit_price: price }),
        ...(note.trim() && { note: note.trim() }),
      };

      const requestData = {
        command_type_name: commandType,
        payload,
      };

      console.log("[ManualOrderRow] Sending command:", {
        executionId,
        requestData,
        isFutures,
        direction,
        orderType,
      });

      const command = await tdApi.createExecutionCommand(executionId, requestData);

      if (!command?.id) {
        throw new Error("Command created but no ID returned");
      }

      console.log("[ManualOrderRow] Command sent:", {
        commandId: command.id,
        executionId,
        payload,
        command,
      });

      showToast({
        type: "info",
        title: `Command #${command.id} Sent`,
        message: `${directionLabel} ${orderTypeLabel} - ${amountLabel}${priceLabel}`,
        timeout: 5000,
      });

      // Start polling for command status (runs in background)
      pollCommandStatus(command.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[ManualOrderRow] Failed to send command:", {
        executionId,
        error: err,
      });
      showToast({
        type: "danger",
        title: "Command Failed",
        message: `Failed to send order command: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    amount,
    amountType,
    orderType,
    limitPrice,
    direction,
    note,
    isFutures,
    executionId,
    symbolName,
    quoteCurrency,
    showToast,
    confirmModal,
    pollCommandStatus,
  ]);

  const isDisabled = loading || polling;

  return (
    <div
      className={`ManualOrderRow position-grid border-bottom ${rowIndex % 2 === 0 ? "bg-light-subtle" : ""}`}
    >
      <div className="PositionCell p-2" style={{ gridColumn: "1 / -1" }}>
        <div
          className="d-flex flex-wrap align-items-center gap-2 p-2 rounded"
          style={{ border: "1px solid var(--cui-border-color)" }}
        >
          <span className="text-muted small fw-bold me-2">
            {isFutures ? "Futures Order:" : "New Order:"}
          </span>

          {/* Direction Toggle - Futures only */}
          {isFutures && (
            <CButtonGroup size="sm">
              <CButton
                color={direction === "long" ? "success" : "secondary"}
                variant={direction === "long" ? undefined : "outline"}
                onClick={() => setDirection("long")}
                disabled={isDisabled}
              >
                Long
              </CButton>
              <CButton
                color={direction === "short" ? "danger" : "secondary"}
                variant={direction === "short" ? undefined : "outline"}
                onClick={() => setDirection("short")}
                disabled={isDisabled}
              >
                Short
              </CButton>
            </CButtonGroup>
          )}

          {/* Amount Type Select */}
          <CFormSelect
            size="sm"
            value={amountType}
            onChange={(e) => setAmountType(e.target.value as AmountType)}
            disabled={isDisabled}
            style={{ width: "100px" }}
            aria-label="Amount type"
          >
            <option value="quote">{quoteCurrency}</option>
            <option value="position">{isFutures ? "Contracts" : "Units"}</option>
          </CFormSelect>

          {/* Amount Input */}
          <CFormInput
            type="number"
            size="sm"
            placeholder="Amount"
            aria-label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={isDisabled}
            style={{ width: "100px" }}
            min="0"
            step="any"
          />
          {showLeverageHint && (
            <CTooltip content={`Margin locked = Size ÷ ${leverage}`}>
              <small className="text-warning">{leverage}× leverage</small>
            </CTooltip>
          )}

          {/* Order Type Select */}
          <CFormSelect
            size="sm"
            value={orderType}
            onChange={(e) => setOrderType(e.target.value as OrderType)}
            disabled={isDisabled}
            style={{ width: "100px" }}
            aria-label="Order type"
          >
            <option value="market">Market</option>
            <option value="limit">Limit</option>
          </CFormSelect>

          {/* Limit Price Input */}
          <CFormInput
            type="number"
            size="sm"
            placeholder="Price"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value)}
            disabled={isDisabled || orderType === "market"}
            style={{ width: "100px", opacity: orderType === "market" ? 0.5 : 1 }}
            min="0"
            step="any"
          />

          {/* Note Input */}
          <CFormInput
            type="text"
            size="sm"
            placeholder="Note (optional)"
            aria-label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isDisabled}
            style={{ width: "150px" }}
            maxLength={500}
          />

          {/* Submit Button - Changes based on spot/futures mode */}
          <CButton
            color={isFutures ? (direction === "long" ? "success" : "danger") : "success"}
            size="sm"
            onClick={handleSubmit}
            disabled={isDisabled || !amount}
            className="px-3"
          >
            {loading ? (
              <CSpinnerText text="Sending..." />
            ) : polling ? (
              <CSpinnerText text="Processing..." />
            ) : isFutures ? (
              direction === "long" ? (
                "Open Long"
              ) : (
                "Open Short"
              )
            ) : (
              "BUY"
            )}
          </CButton>
        </div>
      </div>
    </div>
  );
};
